/**
 * Omni-Torn levels and how a player gets one. Shared by the server (which keeps the
 * grants) and the extension (which shows the level and gates features).
 *
 * Where a level can come from, strongest first:
 *  1. Built in: the owner (OWNER ids) — always owner, nothing can change that.
 *  2. The owner's grant to that player — overrides everything below, including
 *     a paid subscription (and can be "Basic", to take a level away).
 *  3. The owner's grant to the player's faction — only while they're in it — and
 *     a subscription (a personal tier). If both apply, the higher level wins
 *     (LEVEL_RANK), so a faction grant never undercuts someone's own tier.
 *  4. Otherwise: basic.
 * Grants and subscriptions can run out (`until`); expired ones are simply ignored.
 *
 * Tiers (owner's decision 12, 2026-11-01: "an owners section where I can decide which features are available to which
 * tiers … a faction tier version where one person can pay for a faction"): the levels ARE the tiers. A personal tier is a
 * `Subscription`; a faction tier is a faction `Grant` with `paidBy` (the player paying) and an end date. Both are entered
 * by hand on the website's Owner page for now — nothing takes payments (Torn's rules on paid tools and each outside
 * service's commercial-use terms come first). "Who gets what" (`AccessTable`) is the one features × tiers table: the
 * extension's features, the website's sections and the Discord bot's commands and parts (bot-features.ts).
 */

export const LEVELS = ['owner', 'developer', 'alpha', 'beta', 'exclusive', 'basic'] as const;
export type Level = (typeof LEVELS)[number];

/** Levels the owner can grant (owner is only ever built in). */
export const GRANTABLE_LEVELS = ['developer', 'alpha', 'beta', 'exclusive', 'basic'] as const;
export type GrantableLevel = (typeof GRANTABLE_LEVELS)[number];

/** Which level is "higher" when a faction grant and a subscription both apply. */
export const LEVEL_RANK: Record<Level, number> = { owner: 6, developer: 5, exclusive: 4, alpha: 3, beta: 2, basic: 1 };

export function isLevel(x: unknown): x is Level {
  return typeof x === 'string' && (LEVELS as readonly string[]).includes(x);
}
export function isGrantable(x: unknown): x is GrantableLevel {
  return typeof x === 'string' && (GRANTABLE_LEVELS as readonly string[]).includes(x);
}

/** The owner giving a level to one player or a whole faction, free, maybe for a while. */
export interface Grant {
  id: string;
  kind: 'player' | 'faction';
  /** Player id or faction id. */
  target: number;
  level: GrantableLevel;
  /** When it runs out (ms since 1970), or null for no end. */
  until: number | null;
  note: string;
  createdAt: number;
  /**
   * A faction tier (decision 12): the player who pays for this faction's tier. Only on faction grants; null/missing: a
   * free grant. Seen only by the owner (never in /api/level or anywhere a player looks).
   */
  paidBy?: number | null;
}

/**
 * A personal tier (decision 12: "Anyone with an omni sub …"): one player's level until a date. Entered by the owner by
 * hand on the website's Owner page (`plan` 'by hand') — no payments are taken. Written by the server only.
 */
export interface Subscription {
  playerId: number;
  level: GrantableLevel;
  until: number;
  /** Which plan, and the payment it came from (log/event id) — 'by hand' and '' for the owner's own entries. */
  plan: string;
  ref: string;
  createdAt: number;
  /** The owner's own note. */
  note?: string;
}

/** What `plan` says on a subscription the owner entered himself. */
export const BY_HAND_PLAN = 'by hand';

export interface LevelData {
  grants: Grant[];
  subscriptions: Subscription[];
}

export type LevelSource = 'built-in' | 'player-grant' | 'faction-grant' | 'subscription' | 'none';

export interface ResolvedLevel {
  level: Level;
  source: LevelSource;
  /** When this level runs out (null: no end). */
  until: number | null;
  /** The grant it came from, if any. */
  grantId: string | null;
}

const BASIC: ResolvedLevel = { level: 'basic', source: 'none', until: null, grantId: null };

export function isActive(x: { until: number | null }, now: number): boolean {
  return x.until === null || x.until > now;
}

/** Newest first, so a later grant for the same target wins. */
function newest<T extends { createdAt: number }>(list: T[]): T | undefined {
  return [...list].sort((a, b) => b.createdAt - a.createdAt)[0];
}

export function resolveLevel(who: { playerId: number | null; factionId: number | null }, data: LevelData, now: number, ownerIds: readonly number[] = []): ResolvedLevel {
  const { playerId, factionId } = who;
  if (playerId === null) return BASIC;
  if (ownerIds.includes(playerId)) return { level: 'owner', source: 'built-in', until: null, grantId: null };

  const own = newest(data.grants.filter((g) => g.kind === 'player' && g.target === playerId && isActive(g, now)));
  if (own) return { level: own.level, source: 'player-grant', until: own.until, grantId: own.id };

  const candidates: ResolvedLevel[] = [];
  if (factionId !== null) {
    const fac = newest(data.grants.filter((g) => g.kind === 'faction' && g.target === factionId && isActive(g, now)));
    if (fac) candidates.push({ level: fac.level, source: 'faction-grant', until: fac.until, grantId: fac.id });
  }
  const sub = [...data.subscriptions].filter((s) => s.playerId === playerId && isActive(s, now)).sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level] || b.until - a.until)[0];
  if (sub) candidates.push({ level: sub.level, source: 'subscription', until: sub.until, grantId: null });
  // Higher level wins; on a tie the owner's grant counts (it's listed first).
  return candidates.sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level])[0] ?? BASIC;
}

const ID = /^[1-9]\d{0,9}$/;

/** Clean a list of grants from outside (a file, a request): bad entries are dropped. */
export function cleanGrants(raw: unknown, now: number = Date.now()): Grant[] {
  if (!Array.isArray(raw)) return [];
  const out: Grant[] = [];
  const seen = new Set<string>();
  for (const g of raw.slice(0, 5000) as Record<string, unknown>[]) {
    if (!g || typeof g !== 'object') continue;
    const kind = g.kind === 'faction' ? 'faction' : g.kind === 'player' ? 'player' : null;
    const target = Number(g.target);
    if (!kind || !ID.test(String(g.target)) || !isGrantable(g.level)) continue;
    const until = g.until === null || g.until === undefined ? null : Number(g.until);
    if (until !== null && !Number.isFinite(until)) continue;
    let id = typeof g.id === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(g.id) ? g.id : `${kind[0]}${target}-${Math.round(Number(g.createdAt) || now).toString(36)}`;
    while (seen.has(id)) id += 'x';
    seen.add(id);
    out.push({
      id,
      kind,
      target,
      level: g.level,
      until,
      note: typeof g.note === 'string' ? g.note.slice(0, 200) : '',
      createdAt: Number.isFinite(Number(g.createdAt)) && Number(g.createdAt) > 0 ? Number(g.createdAt) : now,
      ...(kind === 'faction' && g.paidBy !== null && g.paidBy !== undefined && ID.test(String(g.paidBy)) ? { paidBy: Number(g.paidBy) } : {}),
    });
  }
  return out;
}

export function cleanSubscriptions(raw: unknown): Subscription[] {
  if (!Array.isArray(raw)) return [];
  return (raw.slice(0, 5000) as Record<string, unknown>[])
    .filter((s) => s && typeof s === 'object' && ID.test(String(s.playerId)) && isGrantable(s.level) && Number.isFinite(Number(s.until)) && Number(s.until) > 0)
    .map((s) => ({
      playerId: Number(s.playerId),
      level: s.level as GrantableLevel,
      until: Number(s.until),
      plan: String(s.plan ?? '').slice(0, 60),
      ref: String(s.ref ?? '').slice(0, 100),
      createdAt: Number(s.createdAt) || 0,
      ...(typeof s.note === 'string' && s.note ? { note: s.note.slice(0, 200) } : {}),
    }));
}

/**
 * A faction's own tier (decision 12: "a faction tier … access to certain features at each level. This should effect the
 * bot too"): the owner's newest active grant to that faction, else basic — for what runs for a faction as a whole (the
 * bot's faction alerts), where no one player is asking.
 */
export function resolveFactionTier(factionId: number | null, data: LevelData, now: number): GrantableLevel {
  if (factionId === null) return 'basic';
  return newest(data.grants.filter((g) => g.kind === 'faction' && g.target === factionId && isActive(g, now)))?.level ?? 'basic';
}

/**
 * The owner's entries on the Owner page's "Subscriptions and faction tiers": personal tiers (subscriptions) and faction
 * tiers (faction grants with a payer). Each needs an end date in the future when entered; one per player / per faction.
 */
export interface PaidTiers {
  subscriptions: Subscription[];
  factionTiers: Grant[];
}

/** The faction grants that are faction tiers (someone pays for them). */
export const isFactionTier = (g: Grant): boolean => g.kind === 'faction' && typeof g.paidBy === 'number';

/** Old v0.15 `{ "<player id>": "<level>" }` lists become player grants with no end. */
export function grantsFromRoles(roles: unknown, now: number): Grant[] {
  if (!roles || typeof roles !== 'object' || Array.isArray(roles)) return [];
  return cleanGrants(
    Object.entries(roles as Record<string, unknown>).map(([id, level]) => ({ kind: 'player', target: id, level, until: null, note: 'from v0.15 levels', createdAt: now })),
    now,
  );
}

// ---------------------------------------------------------------- who gets what

/** Per feature id: the levels that may use it (owner always may). Missing: the feature's own default. */
export type AccessTable = Record<string, Level[]>;

/**
 * "Omni-Torn itself" in the table: the levels that may use anything beyond signing in and giving their
 * key for tracking (owner's call 2026-10-18, v0.82.0: "until I say so no one I don't grant access to
 * should be able to use the extension for anything other than signing in and offering their data").
 * Not listed: every level the owner gives (not Basic). The owner opens it to everyone by ticking Basic.
 */
export const OMNI_TORN_ACCESS = 'omni-torn';
export const OMNI_TORN_DEFAULT: readonly Level[] = ['developer', 'alpha', 'beta', 'exclusive'];

/**
 * Settings sync (owner's call 2026-10-22: "feel free to add a permission for it but I do plan on syncing everyone's
 * settings"): its own row in "who gets what", every level by default, and not behind "Omni-Torn itself" — keeping your
 * settings with your sign-in is part of signing in. The owner can still take it from a level or one player.
 */
export const SETTINGS_SYNC_ACCESS = 'settings-sync';
export const SETTINGS_SYNC_DEFAULT: readonly Level[] = LEVELS;

/**
 * What a feature (or a website section) needs by default — none: every level. The owner's table
 * overrides it per feature; a player's own overrides (below) override both.
 */
export type Access = 'owner' | 'dev' | 'alpha' | 'beta' | 'exclusive';

const ALLOWED: Record<Access, readonly Level[]> = {
  owner: ['owner'],
  dev: ['owner', 'developer'],
  alpha: ['owner', 'developer', 'alpha'],
  beta: ['owner', 'developer', 'alpha', 'beta'],
  exclusive: ['owner', 'developer', 'exclusive'],
};

/** The levels an access need lets in, by default. */
export function levelsFor(access: Access | undefined): readonly Level[] {
  return access ? ALLOWED[access] : LEVELS;
}

export function canUse(level: Level, access: Access | undefined): boolean {
  return levelsFor(access).includes(level);
}

// ---------------------------------------------------------------- one player's own overrides

/**
 * "This player" in Owner → Who gets what (owner's call 2026-10-20): a custom set for one player —
 * feature ids (and `omni-torn` itself, and `web-…` sections) they get whatever their level says
 * (`allow`), or never get (`deny`). Kept on the server (access.json `players`), handed to that player
 * with their level; nobody else sees it.
 */
export interface PlayerAccess {
  allow: string[];
  deny: string[];
  note: string;
  updatedAt: number;
}
export type PlayerAccessTable = Record<string, PlayerAccess>;
/** What one player got (the extension and website keep only their own). */
export interface MyAccess {
  allow: readonly string[];
  deny: readonly string[];
}
export const NO_OVERRIDES: MyAccess = Object.freeze({ allow: [], deny: [] });

const FEATURE_ID = /^[a-z0-9-]{1,60}$/;
const PLAYER_ID = /^[1-9]\d{0,9}$/;

function cleanIds(raw: unknown): string[] {
  return Array.isArray(raw) ? [...new Set(raw.filter((x): x is string => typeof x === 'string' && FEATURE_ID.test(x)))].slice(0, 500) : [];
}

export function cleanPlayerAccess(raw: unknown): PlayerAccessTable {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: PlayerAccessTable = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>).slice(0, 5000)) {
    if (!PLAYER_ID.test(id) || !v || typeof v !== 'object') continue;
    const o = v as Record<string, unknown>;
    const allow = cleanIds(o.allow);
    const deny = cleanIds(o.deny).filter((x) => !allow.includes(x));
    if (!allow.length && !deny.length) continue;
    out[id] = { allow, deny, note: typeof o.note === 'string' ? o.note.slice(0, 200) : '', updatedAt: Number.isFinite(Number(o.updatedAt)) ? Number(o.updatedAt) : 0 };
  }
  return out;
}

export function cleanMyAccess(raw: unknown): MyAccess {
  if (!raw || typeof raw !== 'object') return NO_OVERRIDES;
  const o = raw as Record<string, unknown>;
  const allow = cleanIds(o.allow);
  return { allow, deny: cleanIds(o.deny).filter((x) => !allow.includes(x)) };
}

/** The overrides for one player (none: nothing special). */
export function overridesFor(players: PlayerAccessTable | null | undefined, playerId: number | null | undefined): MyAccess {
  const p = playerId != null ? players?.[String(playerId)] : undefined;
  return p ? { allow: p.allow, deny: p.deny } : NO_OVERRIDES;
}

/**
 * May this level use Omni-Torn beyond signing in and giving their key? The owner always; a player denied
 * `omni-torn` never; one allowed it always; otherwise the table's row (or its default) decides by level.
 */
export function mayUseOmniTorn(level: Level, table: AccessTable | null | undefined, mine: MyAccess = NO_OVERRIDES): boolean {
  if (level === 'owner') return true;
  if (mine.deny.includes(OMNI_TORN_ACCESS)) return false;
  if (mine.allow.includes(OMNI_TORN_ACCESS)) return true;
  return (table?.[OMNI_TORN_ACCESS] ?? OMNI_TORN_DEFAULT).includes(level);
}

/**
 * May this player use this feature (or website section)? The owner always may. Then their own overrides:
 * denied → no; allowed → yes (even without Omni-Torn access — the owner gave them that one thing).
 * Otherwise they need access to Omni-Torn itself, and the owner's table row if there is one, else the
 * feature's own default.
 */
export function canUseFeature(level: Level, feature: { id: string; access?: Access }, table: AccessTable | null | undefined = {}, mine: MyAccess = NO_OVERRIDES): boolean {
  if (level === 'owner') return true;
  if (mine.deny.includes(feature.id)) return false;
  if (mine.allow.includes(feature.id) && feature.access !== 'owner') return true;
  if (!mayUseOmniTorn(level, table, mine)) return false;
  const listed = table?.[feature.id];
  return listed ? listed.includes(level) : canUse(level, feature.access);
}

/** Said wherever someone without access lands. */
export const NOT_OPEN_TEXT = 'Omni-Torn isn’t open to everyone yet — woyzer gives access to players one by one. You’re signed in: your key and data are kept ready (Account), so everything works for you the moment you get access.';

export function cleanAccess(raw: unknown): AccessTable {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: AccessTable = {};
  for (const [id, levels] of Object.entries(raw as Record<string, unknown>).slice(0, 500)) {
    if (!/^[a-z0-9-]{1,60}$/.test(id) || !Array.isArray(levels)) continue;
    out[id] = [...new Set(levels.filter(isLevel).filter((l) => l !== 'owner'))];
  }
  return out;
}

/** May this player keep their settings on the server and sync them between devices (SETTINGS_SYNC_ACCESS)? */
export function maySyncSettings(level: Level, table: AccessTable | null | undefined, mine: MyAccess = NO_OVERRIDES): boolean {
  if (level === 'owner') return true;
  if (mine.deny.includes(SETTINGS_SYNC_ACCESS)) return false;
  if (mine.allow.includes(SETTINGS_SYNC_ACCESS)) return true;
  return (table?.[SETTINGS_SYNC_ACCESS] ?? SETTINGS_SYNC_DEFAULT).includes(level);
}
